import React, { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { API_BASE_URL } from "../config";

const KINDS = [
  { id: "feedback", label: "Feedback" },
  { id: "contact", label: "Contact us" },
  { id: "bug", label: "Report a bug" },
];

const field =
  "w-full rounded-xl bg-[var(--ab-surface)] border border-[var(--ab-border)] px-4 py-3 text-base sm:text-sm text-[var(--ab-text)] placeholder-[var(--ab-faint)] focus:outline-none focus:border-[var(--ab-accent)] transition";

export default function FeedbackForm() {
  const [form, setForm] = useState({ kind: "feedback", name: "", email: "", message: "", website: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 3) {
      setStatus("error");
      setError("Please write a message.");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, page: window.location.pathname }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Could not send your message.");
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setError(err.message || "Could not send your message.");
    }
  };

  return (
    <section id="contact" className="scroll-mt-20 max-w-6xl mx-auto px-4 sm:px-8 py-20 md:py-28">
      <div className="grid gap-10 md:gap-16 md:grid-cols-[1fr_1.3fr]">
        <div className="space-y-3">
          <span className="font-mono text-xs uppercase tracking-[0.12em] text-[var(--ab-accent)]">Contact & Feedback</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Tell us what you think</h2>
          <p className="text-[var(--ab-muted)] leading-relaxed">
            Questions, ideas, a bug, or a feature you want — send it here and the team reads every message.
            Leave your email if you'd like a reply.
          </p>
        </div>

        {status === "sent" ? (
          <div className="rounded-2xl border border-[var(--ab-border)] bg-[var(--ab-surface)] p-8 flex flex-col items-center justify-center text-center gap-3">
            <CheckCircle2 size={36} className="text-[var(--ab-up)]" />
            <h3 className="text-lg font-semibold">Thanks — message received</h3>
            <p className="text-sm text-[var(--ab-muted)]">
              {form.email ? "We'll get back to you by email." : "We appreciate you taking the time."}
            </p>
            <button
              onClick={() => { setForm({ ...form, message: "" }); setStatus("idle"); }}
              className="mt-2 text-sm text-[var(--ab-accent)] hover:underline"
            >
              Send another
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {KINDS.map((k) => (
                <button
                  type="button"
                  key={k.id}
                  onClick={() => setForm({ ...form, kind: k.id })}
                  className={`px-3.5 py-2 rounded-lg text-sm border transition ${
                    form.kind === k.id
                      ? "bg-[var(--ab-accent-soft)] border-[var(--ab-accent)] text-[var(--ab-text)]"
                      : "border-[var(--ab-border)] text-[var(--ab-muted)] hover:text-[var(--ab-text)]"
                  }`}
                >
                  {k.label}
                </button>
              ))}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input className={field} placeholder="Name (optional)" value={form.name} onChange={set("name")} maxLength={120} />
              <input className={field} type="email" placeholder="Email (for a reply)" value={form.email} onChange={set("email")} />
            </div>
            {/* Honeypot: hidden from people, filled by bots. */}
            <input
              type="text" tabIndex={-1} autoComplete="off" aria-hidden="true"
              value={form.website} onChange={set("website")}
              className="absolute -left-[9999px] h-0 w-0 opacity-0"
            />
            <textarea
              className={`${field} min-h-[140px] resize-y`}
              placeholder="Your message"
              value={form.message}
              onChange={set("message")}
              maxLength={5000}
              required
            />
            {status === "error" && <p className="text-sm text-red-500">{error}</p>}
            <button
              type="submit"
              disabled={status === "sending"}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--ab-accent)] px-5 py-3 text-sm font-semibold text-[var(--ab-on-accent)] hover:bg-[var(--ab-accent-hover)] transition disabled:opacity-60"
            >
              <Send size={15} /> {status === "sending" ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
